import { useMemo } from 'react';
import { ResponsiveContainer, LineChart, Line, XAxis, YAxis, Tooltip, CartesianGrid, BarChart, Bar, PieChart, Pie, Cell, Legend } from 'recharts';
import { ChartLine } from '@phosphor-icons/react';
import { formatJSTShort } from '@/utils/datetime';

const COLORS = ['#0D9488', '#0891B2', '#F59E0B', '#8B5CF6', '#EF4444', '#10B981', '#6366F1', '#EC4899', '#84CC16', '#64748B'];

function StatChip({ label, value }) {
  return (
    <div className="px-3 py-1.5 rounded-lg bg-teal-50 border border-teal-100">
      <span className="text-[10px] text-teal-600 block">{label}</span>
      <span className="text-sm font-bold text-teal-800 tabular-nums">{value}</span>
    </div>
  );
}

function NumberChart({ field, responses }) {
  const points = useMemo(() => responses
    .map((r) => ({ at: formatJSTShort(r.created_at), v: Number((r.answers || {})[field.id]) }))
    .filter((p) => !Number.isNaN(p.v))
    .reverse(), [responses, field.id]);
  if (points.length === 0) return null;
  const vals = points.map((p) => p.v);
  const avg = vals.reduce((a, b) => a + b, 0) / vals.length;
  return (
    <div className="bg-white rounded-xl border border-zinc-200 p-4" data-testid={`eng-chart-${field.id}`}>
      <p className="text-sm font-semibold text-zinc-800 mb-2">{field.label}</p>
      <div className="flex flex-wrap gap-2 mb-3">
        <StatChip label="件数" value={vals.length} />
        <StatChip label="平均" value={avg.toFixed(2)} />
        <StatChip label="最小" value={Math.min(...vals)} />
        <StatChip label="最大" value={Math.max(...vals)} />
      </div>
      <ResponsiveContainer width="100%" height={220}>
        <LineChart data={points} margin={{ top: 5, right: 10, bottom: 5, left: -10 }}>
          <CartesianGrid strokeDasharray="3 3" stroke="#f1f5f9" />
          <XAxis dataKey="at" tick={{ fontSize: 10, fill: '#a1a1aa' }} />
          <YAxis tick={{ fontSize: 10, fill: '#a1a1aa' }} domain={['auto', 'auto']} />
          <Tooltip contentStyle={{ fontSize: 12, borderRadius: 8 }} />
          <Line type="monotone" dataKey="v" name={field.label} stroke="#0D9488" strokeWidth={2} dot={{ r: 3 }} />
        </LineChart>
      </ResponsiveContainer>
    </div>
  );
}

const RAD = Math.PI / 180;
const renderPieValueLabel = ({ cx, cy, midAngle, innerRadius, outerRadius, percent, value }) => {
  if (percent < 0.05) return null;
  const r = innerRadius + (outerRadius - innerRadius) * 0.6;
  const x = cx + r * Math.cos(-midAngle * RAD);
  const y = cy + r * Math.sin(-midAngle * RAD);
  return (
    <text x={x} y={y} fill="#fff" fontSize={13} fontWeight={700} textAnchor="middle" dominantBaseline="central">
      {value}
    </text>
  );
};

function ChoiceChart({ field, responses }) {
  const data = useMemo(() => {
    const counts = {};
    (field.options || []).forEach((o) => { counts[o] = 0; });
    responses.forEach((r) => {
      const v = (r.answers || {})[field.id];
      if (Array.isArray(v)) v.forEach((x) => { if (x in counts) counts[x] += 1; });
      else if (v != null && v !== '' && v in counts) counts[v] += 1;
    });
    return Object.entries(counts).map(([name, value]) => ({ name, value }));
  }, [responses, field]);
  const total = data.reduce((a, b) => a + b.value, 0);
  if (total === 0) return null;
  const isMulti = field.type === 'checkbox';
  return (
    <div className="bg-white rounded-xl border border-zinc-200 p-4" data-testid={`eng-chart-${field.id}`}>
      <p className="text-sm font-semibold text-zinc-800 mb-2">{field.label} <span className="text-[11px] text-zinc-400 font-normal">（{total}件）</span></p>
      <ResponsiveContainer width="100%" height={220}>
        {isMulti ? (
          <BarChart data={data} layout="vertical" margin={{ top: 5, right: 20, bottom: 5, left: 10 }}>
            <CartesianGrid strokeDasharray="3 3" stroke="#f1f5f9" horizontal={false} />
            <XAxis type="number" allowDecimals={false} tick={{ fontSize: 10, fill: '#a1a1aa' }} />
            <YAxis type="category" dataKey="name" width={90} tick={{ fontSize: 11, fill: '#52525b' }} />
            <Tooltip contentStyle={{ fontSize: 12, borderRadius: 8 }} />
            <Bar dataKey="value" name="件数" fill="#0D9488" radius={[0, 4, 4, 0]} barSize={18} />
          </BarChart>
        ) : (
          <PieChart margin={{ top: 8, right: 8, bottom: 0, left: 8 }}>
            <Pie data={data.filter((d) => d.value > 0)} dataKey="value" nameKey="name" cx="50%" cy="46%"
              outerRadius={75} label={renderPieValueLabel} labelLine={false}>
              {data.filter((d) => d.value > 0).map((_, i) => <Cell key={i} fill={COLORS[i % COLORS.length]} />)}
            </Pie>
            <Legend
              wrapperStyle={{ fontSize: 11 }}
              formatter={(name, entry) => {
                const pct = total > 0 ? Math.round(((entry?.payload?.value || 0) / total) * 100) : 0;
                return `${name}（${entry?.payload?.value ?? 0}件 · ${pct}%）`;
              }}
            />
            <Tooltip contentStyle={{ fontSize: 12, borderRadius: 8 }} formatter={(v, n) => [`${v}件`, n]} />
          </PieChart>
        )}
      </ResponsiveContainer>
    </div>
  );
}

export const EngFormCharts = ({ form, responses }) => {
  const chartable = (form.fields || []).filter((f) => ['number', 'select', 'radio', 'checkbox'].includes(f.type));
  if (chartable.length === 0) {
    return (
      <div className="bg-white rounded-xl border border-dashed border-zinc-300 p-10 text-center" data-testid="eng-charts-empty">
        <ChartLine size={40} weight="duotone" className="text-zinc-300 mx-auto mb-2" />
        <p className="text-sm text-zinc-500">グラフ化できる項目がありません</p>
        <p className="text-xs text-zinc-400 mt-1">数値・プルダウン・単一選択・複数選択の項目が対象です</p>
      </div>
    );
  }
  return (
    <div className="grid grid-cols-1 lg:grid-cols-2 gap-4" data-testid="eng-charts-grid">
      {chartable.map((f) => (
        f.type === 'number'
          ? <NumberChart key={f.id} field={f} responses={responses} />
          : <ChoiceChart key={f.id} field={f} responses={responses} />
      ))}
    </div>
  );
};
